import { useThemeColor } from "@/hooks/useThemeColor";
import memoize from "lodash/memoize";
import { useMemo } from "react";
import { Platform, StyleSheet } from "react-native";
import { resolvedBreakpoint } from "@/helper/Resolver";

const handleButtonSizes = memoize((size) => {

    let styles = {
        button: {
            backgroundColor: 'transparent',
        },
        text: {
            fontSize: 16,
            color: 'white'
        }
    }

    switch(size) {
        case '2xs':
            styles.button.paddingHorizontal = 8;
            styles.button.paddingVertical = 3;
            styles.text.fontSize = 9;
            break;

        case 'xs':
            styles.button.paddingHorizontal = 10;
            styles.button.paddingVertical = 5;
            styles.text.fontSize = 11;
            break;

        case 'sm':
            styles.button.paddingHorizontal = 12;
            styles.button.paddingVertical = 10;
            styles.text.fontSize = 12;
            break;

        case 'md':
            styles.button.paddingHorizontal = 12;
            styles.button.paddingVertical = 10;
            styles.text.fontSize = 16;
            break;

        case 'lg':
            styles.button.paddingHorizontal = 15;
            styles.button.paddingVertical = 16;
            styles.text.fontSize = 20;
            break;

        case 'xl':
            styles.button.paddingHorizontal = 20;
            styles.button.paddingVertical = 18;
            styles.text.fontSize = 25;
            break;
            break;

        case '2xl':
            styles.button.paddingHorizontal = 20;
            styles.button.paddingVertical = 18;
            styles.text.fontSize = 30;
            break;

        default:
            break;
    }

    return styles;
});

const styles = StyleSheet.create({
    button: {
        textAlign: 'center',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
    },
    text: {
        ...Platform.select({
            native: {
                textAlign: 'center',
            },
            default: {
                textAlign: 'left',
            }
        }),
        textTransform: 'uppercase',
    },
    iconRow: {
        flex: 1,
        flexDirection: 'row',
        
        
        ...Platform.select({
            native: {
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
            },
            default: {
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
            }
        }),
    }
});


export const useStyleResolver = (options) => {

    const {
        style = {},
        textStyle = {},
        iconRowStyle = {},
        size,
        variant = null,
        colors,
        disabled = false,
    } = options ?? {};

    
    const sizes = useMemo(() => {
        const resolved = resolvedBreakpoint(size);
        return handleButtonSizes(resolved);
    }, [size]);
    
    const usedColor = useThemeColor(colors, 'background');
    
    const pressStyle = useMemo(() => {

        let variantStyle = { ...style };
        const _color = usedColor ?? style?.backgroundColor ?? 'black';

        if (variant == 'outline') {

            variantStyle = {
                ...variantStyle,
                backgroundColor: 'transparent',
                borderWidth: 2,
                borderColor: _color,
            }

        }

        if (variant == 'outlineActive') {
            
            variantStyle = {
                ...variantStyle,
                backgroundColor: _color,
                borderWidth: 2,
                borderColor: _color,
            }

        }   

        if (disabled) {
            variantStyle.opacity = .5;    
        }

        return StyleSheet.flatten([styles.button, sizes.button, variantStyle]);
    }, [variant, style, usedColor, disabled]);

    const newColors = useMemo(() => {

        if (variant == 'outline') 
            return {light: 'transparent', dark: 'transparent'};

        return colors;
    }, [colors, variant]);

    const textProps = useMemo(() => {
        let _ = {};
        
        _.light = textStyle?.light;
        _.dark = textStyle?.dark;
        _.style = [styles.text, sizes.text, textStyle];
        
        if (textStyle?.variant)
            _.variant = textStyle?.variant;
        
        if (variant === 'outline') {
            _.light = usedColor ?? style?.backgroundColor ?? 'black';
            _.dark = usedColor ?? style?.backgroundColor ?? 'black';
        }

        return _;
    }, [sizes, textStyle, variant, usedColor, style]);

    const iconRow = useMemo(() => {
        return StyleSheet.flatten([styles.iconRow, iconRowStyle]);
    }, [iconRowStyle]);

    const iconColor = useThemeColor({ light: textProps?.light ?? 'black', dark: textProps?.dark ?? 'white' }, 'background');

    return {
        textProps,
        disabled,
        iconColor,
        pressStyle, 
        newColors,
        iconRow
    }
}

